import React, { useState, useEffect, useRef } from 'react';
import { ConstructorEventUpdateSQLStatus } from '../../../Event/event';

const AngleArcheryGame = () => {
  const angleTypes = [
    { angle: 90, type: 'Right', hint: 'It forms a perfect L shape, exactly 90°.' },
    { angle: 180, type: 'Straight', hint: 'It looks like a flat line, 180° straight.' },
    { angle: 45, type: 'Acute', hint: 'Less than 90°, like a sharp corner.' },
    { angle: 30, type: 'Acute', hint: 'Very small angle, much less than 90°.' },
    { angle: 135, type: 'Obtuse', hint: 'More than 90°, but not straight.' },
    { angle: 120, type: 'Obtuse', hint: 'Bigger than a right angle but less than straight.' },
    { angle: 75, type: 'Acute', hint: 'Almost right but still smaller.' },
    { angle: 160, type: 'Obtuse', hint: 'Very close to a straight line but still an angle.' },
  ];

  const totalQuestions = 8;

  const [score, setScore] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [message, setMessage] = useState('');
  const [zoneStates, setZoneStates] = useState({});
  const [showPlayAgain, setShowPlayAgain] = useState(false);

  const correctSound = useRef(null);
  const wrongSound = useRef(null);

  useEffect(() => {
    resetGame(); // Start game on mount
  }, []);

  const shuffleArray = (array) => {
    return [...array].sort(() => Math.random() - 0.5);
  };

  const resetGame = () => {
    const shuffled = shuffleArray(angleTypes);
    setShuffledQuestions(shuffled);
    setCurrentIndex(0);
    setScore(0);
    setQuestionsAnswered(0);
    setMessage('');
    setZoneStates({});
    setShowPlayAgain(false);
  };

  const handleDrop = (type) => {
    const current = shuffledQuestions[currentIndex];
    const isCorrect = type === current.type;
    const newStates = { [type]: isCorrect ? 'correct' : 'wrong' };
    setZoneStates(newStates);

    if (isCorrect) {
      setMessage('🎯 Great Shot!');
      correctSound.current.play();
      const newScore = score + 1;
      const newAnswered = questionsAnswered + 1;
      setScore(newScore);
      setQuestionsAnswered(newAnswered);

      if (newAnswered >= totalQuestions) {
        setMessage(`🎉 Game Over! Your Score: ${newScore}/${totalQuestions}`);
        setShowPlayAgain(true);
        const responsedata = ConstructorEventUpdateSQLStatus();
        console.log("Event completed", responsedata);
      } else {
        setTimeout(() => {
          setCurrentIndex(currentIndex + 1);
          setZoneStates({});
          setMessage('');
        }, 1000);
      }
    } else {
      setMessage('😓 Missed Target! Try again.');
      wrongSound.current.play();
    }
  };

  const current = shuffledQuestions[currentIndex] || {};

  const styles = {
    body: {
      margin: 0,
      fontFamily: "'Segoe UI', sans-serif",
      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
      backgroundAttachment: 'fixed',
      overflow: 'hidden',
      color: '#fff',
      minHeight: '100vh',
      padding: '1rem',
    },
    h1: {
      textAlign: 'center',
      marginTop: '1rem',
      color: '#ffeb3b',
      textShadow: '2px 2px 10px #000',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '90vh',
      position: 'relative',
    },
    target: {
      width: 400,
      height: 400,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr 1fr',
      gap: 5,
      margin: '2rem 0',
    },
    zone: (type) => ({
      border: '2px dashed #fff',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '1.2rem',
      padding: 10,
      textAlign: 'center',
      background: zoneStates[type] === 'correct'
        ? 'rgba(0, 255, 0, 0.5)'
        : zoneStates[type] === 'wrong'
          ? 'rgba(255, 0, 0, 0.5)'
          : 'rgba(0, 0, 0, 0.4)',
      boxShadow:
        zoneStates[type] === 'correct'
          ? '0 0 15px #00ff00'
          : zoneStates[type] === 'wrong'
            ? '0 0 15px #ff0000'
            : 'none',
      transition: 'background 0.3s ease, box-shadow 0.3s ease',
    }),
    arrow: {
      padding: '10px 20px',
      background: '#ff5722',
      borderRadius: 8,
      fontSize: '1.4rem',
      cursor: 'grab',
      userSelect: 'none',
      boxShadow: '0 0 15px #ff9800',
    },
    hint: {
      position: 'absolute',
      top: 10,
      left: 10,
      fontSize: '1.2rem',
      background: 'rgba(0, 0, 0, 0.5)',
      padding: 10,
      borderRadius: 10,
      maxWidth: 250,
      lineHeight: 1.5,
      boxShadow: '0 0 10px #000',
    },
    score: {
      fontSize: '1.2rem',
      color: '#00e676',
    },
    message: {
      fontSize: '1.3rem',
      marginTop: '0.5rem',
    },
    playAgain: {
      marginTop: '1rem',
      fontSize: '1.2rem',
      padding: '10px 20px',
      background: '#4caf50',
      color: '#fff',
      border: 'none',
      borderRadius: 8,
      cursor: 'pointer',
      boxShadow: '0 0 10px #000',
      display: showPlayAgain ? 'inline-block' : 'none',
    },
  };

  return (
    <div style={styles.body}>
      <h1 style={styles.h1}>🏹 Angle Archery Game</h1>
      <div style={styles.container}>
        <div style={styles.hint}>Hint: {current.hint}</div>
        <div
          style={styles.arrow}
          draggable
          onDragStart={(e) => e.dataTransfer.setData('text/plain', current.type)}
        >
          Angle: {current.angle}°
        </div>

        <div style={styles.target}>
          {['Right', 'Straight', 'Acute', 'Obtuse'].map((type) => (
            <div
              key={type}
              style={styles.zone(type)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(type)}
            >
              {type} Angle
            </div>
          ))}
        </div>

        <div style={styles.score}>Score: {score}</div>
        <div style={styles.message}>{message}</div>
        <button style={styles.playAgain} onClick={resetGame}>
          Play Again
        </button>
      </div>

      <audio ref={correctSound} src="https://www.soundjay.com/buttons/sounds/button-10.mp3" />
      <audio ref={wrongSound} src="https://www.soundjay.com/buttons/sounds/button-16.mp3" />
    </div>
  );
};

export default AngleArcheryGame;
